// packages/web/src/components/teach/ExerciseDetailModal.tsx
import React, { useEffect, useState } from 'react';
import { useExerciseLibraryStore } from '../../stores/exerciseLibraryStore';
import { teacherExerciseApi } from '../../services/teacherExerciseApi';
import { X } from 'lucide-react';
import { MathRenderer } from '../common/MathRenderer.v3';

export const ExerciseDetailModal: React.FC = () => {
  const { selectedExerciseForDetail, setShowDetailModal } = useExerciseLibraryStore();
  const [assignmentDetails, setAssignmentDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedExerciseForDetail?.id) {
      fetchAssignmentDetails();
    }
  }, [selectedExerciseForDetail?.id]);

  const fetchAssignmentDetails = async () => {
    if (!selectedExerciseForDetail) return;
    
    setLoading(true);
    try {
      const response = await teacherExerciseApi.getAssignmentStatus(selectedExerciseForDetail.id);
      setAssignmentDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch assignment details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedExerciseForDetail) return null;

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* 背景遮罩 */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={() => setShowDetailModal(false)}
        />

        {/* 模态窗口 */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* 头部 */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">习题详情</h3>
            <button
              onClick={() => setShowDetailModal(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 内容 */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* 基本信息（无标题） */}
            <div className="mb-6">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">标题</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedExerciseForDetail.title}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500">科目</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {getCategoryLabel(selectedExerciseForDetail.category)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">题目内容</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded border border-gray-200">
                    <MathRenderer
                      content={selectedExerciseForDetail.initial_question}
                      className="text-sm text-gray-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">教学要求</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedExerciseForDetail.teacher_requirement || '无'}
                  </p>
                </div>

                {selectedExerciseForDetail.knowledge_points && 
                 Object.keys(selectedExerciseForDetail.knowledge_points).length > 0 && (
                  <div>
                    <label className="text-xs text-gray-500">知识点</label>
                    <div className="mt-1 space-y-2">
                      {Object.values(selectedExerciseForDetail.knowledge_points).map((kp: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-900">{kp.name}</span>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3].map((star) => (
                              <span
                                key={star}
                                className={`text-lg ${
                                  star <= kp.difficulty ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 布置情况 */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">布置情况</h4>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : assignmentDetails?.is_assigned ? (
                <div className="space-y-2">
                  {assignmentDetails.assignments.map((assignment: any, idx: number) => (
                    <div key={idx} className="p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {assignment.homework_name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            布置时间: {new Date(assignment.assigned_at).toLocaleString('zh-CN')}
                          </p>
                        </div>
                        <span className="text-xs text-gray-600">
                          {assignment.student_count} 名学生
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">该习题尚未布置</p>
              )}
            </div>
          </div>

          {/* 底部 */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => setShowDetailModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
